import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FaSearch, FaFileExcel, FaTimes,
  FaChevronDown, FaChevronUp, FaMoneyBillWave
} from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import './Expense.css';

/** Cr Ledger: Cash, or bank name; if no bank name use payment mode (not Cash) */
const getCrLedgerFromExpense = (exp) => {
  const mode = (exp.paymentMode || 'Cash').trim();
  if (mode.toLowerCase() === 'cash') return 'Cash';
  const bankName = (exp.bankName || exp.bank?.name || '').trim();
  return bankName || mode;
};

const mapApiExpenseToRow = (exp) => {
  const amount = Number(exp.amount) || 0;
  return {
    _id: exp._id,
    date: exp.expenseDate,
    vchNo: '',
    drLedger: (exp.head?.name || '').trim(),
    crLedger: getCrLedgerFromExpense(exp),
    amountDr: amount,
    amountCr: amount,
    narration: (exp.particular || '').trim(),
    college: exp.college,
    paymentMode: exp.paymentMode,
    bankName: exp.bankName,
    bank: exp.bank,
  };
};

const getDrLedger = (exp) => exp.drLedger ?? '';
const getCrLedger = (exp) => exp.crLedger || getCrLedgerFromExpense(exp);
const getVchNo = (exp) => exp.vchNo ?? '';
const getNarration = (exp) => exp.narration ?? '';

const Expense = () => {
  const { user } = useAuth();
  const { colleges: collegesList, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (isScoped && collegesList.length > 0 && !filterCollege) {
      setFilterCollege(collegesList[0]._id);
    }
  }, [isScoped, collegesList, filterCollege]);

  const fetchExpenses = useCallback(async () => {
    if (!filterCollege) {
      setExpenses([]);
      setFetchError('');
      return;
    }

    setLoading(true);
    setFetchError('');
    setExpenses([]);

    try {
      const params = { college: filterCollege, _t: Date.now() };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const { data } = await api.get('/expenses', { params });
      const rows = (data?.data || [])
        .filter((exp) => String(exp.college?._id || exp.college) === String(filterCollege))
        .map(mapApiExpenseToRow);
      rows.sort((a, b) => new Date(a.date) - new Date(b.date));
      setExpenses(rows);
    } catch (err) {
      setFetchError(err.response?.data?.message || 'Failed to load expense data');
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [filterCollege, fromDate, toDate]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleClearFilters = () => {
    setSearchQuery('');
    if (!isScoped) setFilterCollege('');
    setFromDate('');
    setToDate('');
    if (!isScoped) setExpenses([]);
  };

  const filteredExpenses = useMemo(() => {
    const search = searchQuery.toLowerCase().trim();
    return expenses.filter((exp) => {
      if (!search) return true;
      const drLedger = getDrLedger(exp).toLowerCase();
      const crLedger = getCrLedger(exp).toLowerCase();
      const narration = getNarration(exp).toLowerCase();
      const vchNo = getVchNo(exp).toLowerCase();
      return (
        drLedger.includes(search) ||
        crLedger.includes(search) ||
        narration.includes(search) ||
        vchNo.includes(search)
      );
    });
  }, [expenses, searchQuery]);

  const selectedCollege = useMemo(
    () => collegesList.find((c) => String(c._id) === String(filterCollege)),
    [collegesList, filterCollege]
  );

  const totalAmountDr = useMemo(
    () => filteredExpenses.reduce((sum, exp) => sum + (Number(exp.amountDr) || 0), 0),
    [filteredExpenses]
  );

  const totalAmountCr = useMemo(
    () => filteredExpenses.reduce((sum, exp) => sum + (Number(exp.amountCr) || 0), 0),
    [filteredExpenses]
  );

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleExportExcel = () => {
    const headers = [
      'Date', 'VCh No', 'Dr Ledger', 'Cr Ledger', 'Amount Dr', 'Amount Cr', 'Narations',
    ];

    const rows = filteredExpenses.map((exp) => [
      exp.date ? new Date(exp.date).toLocaleDateString('en-GB') : '',
      getVchNo(exp),
      getDrLedger(exp),
      getCrLedger(exp),
      Number(exp.amountDr) || 0,
      Number(exp.amountCr) || 0,
      getNarration(exp),
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 28 },
      { wch: 25 },
      { wch: 14 },
      { wch: 14 },
      { wch: 45 },
    ];

    const filenameParts = ['Expense_Register'];
    if (selectedCollege) {
      const cleanName = selectedCollege.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/__+/g, '_');
      filenameParts.push(cleanName);
    }
    if (fromDate && toDate) filenameParts.push(`${fromDate}_to_${toDate}`);
    else if (fromDate) filenameParts.push(`from_${fromDate}`);
    else if (toDate) filenameParts.push(`to_${toDate}`);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expense Register');
    XLSX.writeFile(wb, `${filenameParts.join('_')}.xlsx`);
  };

  const showSelectCollegeMsg = !filterCollege && !collegesLoading;

  return (
    <div className="tele-export-expense-container">
      <div className="tele-export-expense-header-row">
        <h2 className="tele-export-expense-title">
          <FaMoneyBillWave /> Expense Register Ledger
        </h2>
        <div className="tele-export-expense-export-btns">
          <button
            type="button"
            className="tele-export-expense-btn-excel"
            onClick={handleExportExcel}
            disabled={filteredExpenses.length === 0}
          >
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {filterCollege && filteredExpenses.length > 0 && (
        <div className="tele-export-expense-summary-strip">
          <span>
            Showing <strong>{filteredExpenses.length}</strong> expenses
            {selectedCollege ? <> for <strong>{selectedCollege.name}</strong></> : null}
          </span>
          <div className="tele-export-expense-summary-amounts">
            <span className="tele-export-expense-summary-amount">
              Total Dr: <strong>₹ {totalAmountDr.toLocaleString()}</strong>
            </span>
            <span className="tele-export-expense-summary-amount" style={{ marginLeft: '15px' }}>
              Total Cr: <strong>₹ {totalAmountCr.toLocaleString()}</strong>
            </span>
          </div>
        </div>
      )}

      <div className="tele-export-expense-controls">
        <div className="tele-export-expense-search-wrapper">
          <FaSearch className="tele-export-expense-search-icon" />
          <input
            type="text"
            className="tele-export-expense-search-input"
            placeholder="Search expense heads, bank, particulars..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!filterCollege}
          />
        </div>

        {!isScoped && (
          <select
            className="tele-export-expense-select"
            value={filterCollege}
            onChange={(e) => setFilterCollege(e.target.value)}
            disabled={collegesLoading}
          >
            <option value="">Select College</option>
            {collegesList.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {isScoped && selectedCollege && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 14px',
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#334155',
            }}
          >
            🏫 {selectedCollege.name}
          </span>
        )}

        <input
          type="date"
          className="tele-export-expense-select"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          title="From Date"
          disabled={!filterCollege}
        />

        <input
          type="date"
          className="tele-export-expense-select"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate}
          title="To Date"
          disabled={!filterCollege}
        />

        {(filterCollege && !isScoped) || fromDate || toDate || searchQuery ? (
          <button type="button" className="tele-export-expense-btn-clear" onClick={handleClearFilters}>
            <FaTimes /> Clear
          </button>
        ) : null}
      </div>

      <div className="tele-export-expense-grouped-container">
        {collegesLoading ? (
          <div className="tele-export-expense-state-msg">Loading colleges...</div>
        ) : showSelectCollegeMsg ? (
          <div className="tele-export-expense-state-msg tele-export-expense-empty">
            Please select a college to view expenses for Tally export.
          </div>
        ) : loading ? (
          <div className="tele-export-expense-state-msg">Loading records...</div>
        ) : fetchError ? (
          <div className="tele-export-expense-state-msg tele-export-expense-empty">{fetchError}</div>
        ) : filteredExpenses.length === 0 ? (
          <div className="tele-export-expense-state-msg tele-export-expense-empty">
            No expense entries found for the selected college and date range.
          </div>
        ) : (
          <div className="tele-export-expense-table-group">
            <h3 className="tele-export-expense-group-header">
              <span className="tele-export-expense-college-name">
                🏫 {selectedCollege?.name || 'College'}
              </span>
              <div className="tele-export-expense-group-stats">
                <span className="tele-export-expense-group-count">
                  {filteredExpenses.length} entries
                </span>
                <span className="tele-export-expense-group-total">
                  Total: ₹ {totalAmountDr.toLocaleString()}
                </span>
              </div>
            </h3>
            <div className="tele-export-expense-table-wrapper">
              <table className="tele-export-expense-table">
                <thead>
                  <tr>
                    <th width="55px">S.No</th>
                    <th className="tele-export-expense-col-date">Date</th>
                    <th className="tele-export-expense-col-vch">VCh No</th>
                    <th className="tele-export-expense-col-dr tele-export-expense-align-left">Dr Ledger</th>
                    <th className="tele-export-expense-col-cr tele-export-expense-align-left">Cr Ledger</th>
                    <th className="tele-export-expense-col-amount-dr">Amount Dr</th>
                    <th className="tele-export-expense-col-amount-cr">Amount Cr</th>
                    <th className="tele-export-expense-col-narration tele-export-expense-align-left">Narrations</th>
                    <th className="tele-export-expense-col-expand-header" width="70px">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((exp, index) => (
                    <React.Fragment key={exp._id || index}>
                      <tr className="tele-export-expense-main-row">
                        <td>{index + 1}</td>
                        <td className="tele-export-expense-col-date" style={{ fontWeight: 600, color: '#334155' }}>
                          {fmtDate(exp.date)}
                        </td>
                        <td className="tele-export-expense-col-vch" style={{ fontWeight: 600, color: '#475569' }}>
                          {getVchNo(exp)}
                        </td>
                        <td className="tele-export-expense-col-dr tele-export-expense-align-left" style={{ fontWeight: 700, color: '#1e3a5f' }}>
                          {getDrLedger(exp)}
                        </td>
                        <td className="tele-export-expense-col-cr tele-export-expense-align-left" style={{ fontWeight: 700, color: '#475569' }}>
                          {getCrLedger(exp)}
                        </td>
                        <td className="tele-export-expense-col-amount-dr tele-export-expense-amt-total">
                          ₹ {Number(exp.amountDr || 0).toLocaleString()}
                        </td>
                        <td className="tele-export-expense-col-amount-cr tele-export-expense-amt-total" style={{ color: '#16a34a' }}>
                          ₹ {Number(exp.amountCr || 0).toLocaleString()}
                        </td>
                        <td className="tele-export-expense-col-narration tele-export-expense-align-left">
                          <div className="tele-export-expense-truncate-text">
                            {getNarration(exp)}
                          </div>
                        </td>
                        <td className="tele-export-expense-col-expand-trigger">
                          <button
                            type="button"
                            className="tele-export-expense-expand-btn"
                            onClick={() => setExpandedRow(expandedRow === exp._id ? null : exp._id)}
                            aria-label="Toggle details"
                          >
                            {expandedRow === exp._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </td>
                      </tr>

                      {expandedRow === exp._id && (
                        <tr className="tele-export-expense-expanded-row">
                          <td colSpan="9" style={{ padding: 0 }}>
                            <div className="tele-export-expense-expanded-content">
                              <div className="tele-export-expense-expanded-grid">
                                <div className="tele-export-expense-exp-item tele-export-expense-exp-date">
                                  <span className="tele-export-expense-exp-label">Date</span>
                                  <span className="tele-export-expense-exp-value">{fmtDate(exp.date)}</span>
                                </div>
                                <div className="tele-export-expense-exp-item tele-export-expense-exp-vch">
                                  <span className="tele-export-expense-exp-label">VCh No</span>
                                  <span className="tele-export-expense-exp-value">{getVchNo(exp) || '—'}</span>
                                </div>
                                <div className="tele-export-expense-exp-item tele-export-expense-exp-dr">
                                  <span className="tele-export-expense-exp-label">Dr Ledger</span>
                                  <span className="tele-export-expense-exp-value">{getDrLedger(exp) || '—'}</span>
                                </div>
                                <div className="tele-export-expense-exp-item tele-export-expense-exp-cr">
                                  <span className="tele-export-expense-exp-label">Cr Ledger</span>
                                  <span className="tele-export-expense-exp-value">{getCrLedger(exp) || '—'}</span>
                                </div>
                                <div className="tele-export-expense-exp-item tele-export-expense-exp-amount-dr">
                                  <span className="tele-export-expense-exp-label">Amount Dr</span>
                                  <span className="tele-export-expense-exp-value" style={{ color: '#dc2626', fontWeight: 700 }}>
                                    ₹ {Number(exp.amountDr || 0).toLocaleString()}
                                  </span>
                                </div>
                                <div className="tele-export-expense-exp-item tele-export-expense-exp-amount-cr">
                                  <span className="tele-export-expense-exp-label">Amount Cr</span>
                                  <span className="tele-export-expense-exp-value" style={{ color: '#16a34a', fontWeight: 700 }}>
                                    ₹ {Number(exp.amountCr || 0).toLocaleString()}
                                  </span>
                                </div>
                                <div className="tele-export-expense-exp-item tele-export-expense-exp-narration">
                                  <span className="tele-export-expense-exp-label">Narration</span>
                                  <span className="tele-export-expense-exp-value">{getNarration(exp) || '—'}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Expense;